import { ReactNode, useCallback, useMemo } from "react";
import { useActiveTimeZone } from "./ActiveTimeZoneContext";
import { useLanguage } from "./useLanguage";

export interface FormatDateOptions extends Intl.DateTimeFormatOptions {
  placeholder?: ReactNode;
}

export const dateFormatOptions: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric",
};

// Intl.DateTimeFormatOptions keys that affect the formatter constructor.
// "placeholder" is our own extension and does not.
const FORMAT_KEYS = new Set<string>([
  "calendar",
  "numberingSystem",
  "localeMatcher",
  "timeZone",
  "hour12",
  "hourCycle",
  "weekday",
  "era",
  "year",
  "month",
  "day",
  "hour",
  "minute",
  "second",
  "fractionalSecondDigits",
  "dayPeriod",
  "timeZoneName",
  "formatMatcher",
  "dateStyle",
  "timeStyle",
]);

function hasFormatKeys(options: FormatDateOptions): boolean {
  return Object.keys(options).some((k) => FORMAT_KEYS.has(k));
}

export function useFormatDate(
  defaultOptions: FormatDateOptions = dateFormatOptions,
): (
  date: Date | number | null | undefined,
  options?: FormatDateOptions,
) => ReactNode {
  const language = useLanguage();
  const activeTimeZone = useActiveTimeZone();

  // Merge the active timezone into default options when provided.
  // This means every formatter automatically uses the override timezone.
  const effectiveDefaults = useMemo(
    () =>
      activeTimeZone
        ? { ...defaultOptions, timeZone: activeTimeZone }
        : defaultOptions,
    [defaultOptions, activeTimeZone],
  );

  // Cache one formatter for the default options. Most call sites pass no
  // per-call format options, so this formatter is reused on every call.
  const defaultFormatter = useMemo(
    () => new Intl.DateTimeFormat(language, effectiveDefaults),
    [language, effectiveDefaults],
  );

  return useCallback(
    (date = null, options) => {
      const placeholder =
        options?.placeholder ?? effectiveDefaults?.placeholder ?? "";

      if (!date || isNaN(new Date(date).getTime())) {
        return placeholder;
      }

      // Only build a new formatter when per-call options contain format keys;
      // options with only `placeholder` (or no options at all) reuse the cache.
      const formatter =
        options && hasFormatKeys(options)
          ? new Intl.DateTimeFormat(language, {
              ...effectiveDefaults,
              ...options,
            })
          : defaultFormatter;

      return formatter.format(date instanceof Date ? date : new Date(date));
    },
    [language, effectiveDefaults, defaultFormatter],
  );
}
